import { Box, type SxProps, type Theme } from '@mui/material';
import { type RuleSubjectType } from '@lib/customization/poiRules';
import {
  AlignLeftIcon, BarcodeIcon, CaretSquareDownIcon, ClipboardCheckIcon, HashtagIcon, SlidersHIcon, TasksIcon,
  ToggleOnIcon,
} from 'react-line-awesome';

/** The icons are all the same component shape; naming it keeps `<Icon />` typed. */
type LineAwesomeIcon = typeof AlignLeftIcon;

/**
 * Icon and accent colour per input type, carried over from the customization prototype
 * (a standalone mockup that was never committed and is no longer in the repo). The colour
 * is the only thing that tells a Dropdown from a Slider at a glance in a dense table, so
 * it stays stable across the picker, the field rows and anywhere else a type is named.
 *
 * Icons come from `react-line-awesome` like the rest of the app; a Material icon here
 * reads as foreign next to the edit and delete buttons beside it.
 */
const FIELD_TYPE_META: Record<RuleSubjectType, { icon: LineAwesomeIcon; color: string }> = {
  Text: { icon: AlignLeftIcon, color: '#3b82f6' },
  Number: { icon: HashtagIcon, color: '#8b5cf6' },
  Barcode: { icon: BarcodeIcon, color: '#22b8cf' },
  // The two choice types keep one colour — they are the same kind of field — so the
  // silhouettes have to carry the difference: a boxed caret for the one that opens, a
  // checklist for the one that takes several answers. A boxed caret next to a boxed tick
  // was indistinguishable at this size.
  List: { icon: CaretSquareDownIcon, color: '#34c75e' },
  MultiList: { icon: TasksIcon, color: '#34c75e' },
  Toggle: { icon: ToggleOnIcon, color: '#f5a524' },
  Range: { icon: SlidersHIcon, color: '#f0483c' },
  // Not an input: the step's status, where a rule's picker lists it beside the fields. Grey,
  // since every colour above says which kind of field an entry is, and this is none.
  StepStatus: { icon: ClipboardCheckIcon, color: '#64748b' },
};

/** Same rounded tile as the team/folder icons, tinted with the type's accent. */
export const FieldTypeIcon: React.FC<{ type: RuleSubjectType; size?: number; sx?: SxProps<Theme> }> = ({
  type, size = 24, sx,
}) => {
  const { icon: Icon, color } = FIELD_TYPE_META[type];

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: size > 28 ? '.5rem' : '.375rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${color}24`,
        color,
        fontSize: size * 0.58,
        ...sx,
      }}
    >
      <Icon />
    </Box>
  );
};
